// Opening a notification in the band it belongs to.
//
// Notifications are the PERSON's, across every band they sit on: the bell lists them all and a push reaches the
// phone whichever band is open (ART-259). But most of their routes are band pages — `/booking/shows/{id}`, `/contacts`,
// `/?task=` — and a band page renders the ACTIVE band. Opened from softpitch, a Whitsett show link would look for a
// Whitsett show inside softpitch and find nothing. So a tap names the notification's band, and when that band is not
// the active one the person is asked before the app switches (a switch clears the screen they were on).
//
// Two doors share this: a bell row (NotificationMenu / NotificationsScreen) and a push tap (Bootstrap's deep-link
// navigator, which reads the band off the payload via `translateCorePush`). Both decide with
// {@link bandToSwitchTo} and, when it answers a band, hand off to the root-mounted prompt
// (`NotificationBandPromptHost`) through this store.
import { create } from "zustand";
import type { components } from "@cntrl/api-client";
import { resolveActiveBandId, useActiveBand } from "./activeBand.js";
import { useMe } from "./hooks/useSession.js";
import { registerStoreReset } from "./stores.js";

type MeDocument = components["schemas"]["MeDocument"];

/**
 * Route roots that are not band pages — the person's own inbox, profile and notifications, an org's roster, the
 * partner surface, and the signed-out / one-time pages. Opening one never needs the active band to change.
 */
const BAND_NEUTRAL_ROOTS: ReadonlySet<string> = new Set([
  "messages",
  "notifications",
  "profile",
  "roster",
  "partner",
  "join",
  "scan",
  "login",
  "forgot-password",
  "reset",
  "onboarding",
]);

/** The query-string key a push tap's route carries its band on (stripped before navigating). */
export const NOTIFICATION_BAND_PARAM = "notifBand";

function rootOf(route: string): string {
  return route.replace(/^\//, "").split(/[/?#]/)[0] ?? "";
}

/**
 * The band to switch to before opening `route` for a notification of `bandId`, or null to open it as-is. Null when
 * the route is not a band page, when the notification names no band, when the person holds no seat on it (a
 * friend's band, an invite to a band they have not joined — its page is not theirs to open in), and when it is
 * already the active band.
 */
export function bandToSwitchTo(
  me: MeDocument | undefined,
  remembered: string | null,
  bandId: string | null | undefined,
  route: string,
): string | null {
  if (!me || !bandId || BAND_NEUTRAL_ROOTS.has(rootOf(route))) return null;
  const held = (me.seats ?? []).some((s) => s.kind === "band" && s.bandId === bandId);
  if (!held) return null;
  return resolveActiveBandId(me, remembered) === bandId ? null : bandId;
}

/** Append the notification's band to a push route (see {@link takeNotificationBand}). */
export function withNotificationBand(route: string, bandId: string): string {
  const [path, hash = ""] = route.split("#", 2) as [string, string?];
  const sep = path.includes("?") ? "&" : "?";
  return `${path}${sep}${NOTIFICATION_BAND_PARAM}=${encodeURIComponent(bandId)}${hash ? `#${hash}` : ""}`;
}

/** Split a route into the route to open and the band its push named, removing the band parameter. */
export function takeNotificationBand(route: string): { route: string; bandId: string | null } {
  const hashAt = route.indexOf("#");
  const hash = hashAt >= 0 ? route.slice(hashAt) : "";
  const beforeHash = hashAt >= 0 ? route.slice(0, hashAt) : route;
  const queryAt = beforeHash.indexOf("?");
  if (queryAt < 0) return { route, bandId: null };
  const params = new URLSearchParams(beforeHash.slice(queryAt + 1));
  const bandId = params.get(NOTIFICATION_BAND_PARAM);
  if (bandId === null) return { route, bandId: null };
  params.delete(NOTIFICATION_BAND_PARAM);
  const query = params.toString();
  return { route: `${beforeHash.slice(0, queryAt)}${query ? `?${query}` : ""}${hash}`, bandId };
}

interface NotificationBandPromptState {
  /** The route waiting on the person's answer, and the band it opens in. */
  pending: { route: string; bandId: string } | null;
  ask: (route: string, bandId: string) => void;
  clear: () => void;
}

export const useNotificationBandPrompt = create<NotificationBandPromptState>((set) => ({
  pending: null,
  ask: (route, bandId) => set({ pending: { route, bandId } }),
  clear: () => set({ pending: null }),
}));

// Logout must not leave a question about the previous person's band on screen.
registerStoreReset(() => useNotificationBandPrompt.getState().clear());

/**
 * For a bell row: open `route` for a notification of `bandId` — through `open` when no band change is needed,
 * otherwise by asking first (the root-mounted prompt then switches and opens it). Returns whether it ASKED, so a
 * caller that closes its own panel on open can do the same either way.
 */
export function useOpenNotificationRoute(): (route: string, bandId: string | null | undefined, open: (route: string) => void) => boolean {
  const me = useMe();
  const remembered = useActiveBand((s) => s.activeBandId);
  const ask = useNotificationBandPrompt((s) => s.ask);
  return (route, bandId, open) => {
    const target = bandToSwitchTo(me.data, remembered, bandId, route);
    if (target) {
      ask(route, target);
      return true;
    }
    open(route);
    return false;
  };
}
